import { defineMessages, useIntl } from 'react-intl';

import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { lineHeights } from '~/eds/src/system/theme/lineHeights';
import { typography } from '~/eds/src/tokens/typography';

const messages = defineMessages({
  showSidebarTooltip: {
    id: 'TicketDetailsHeader.showSidebarTooltip',
    defaultMessage: 'Show Sidebar',
    description:
      'Tooltip for the icon button that shows the sidebar in the ticket details header',
  },
  hideSidebarTooltip: {
    id: 'TicketDetailsHeader.hideSidebarTooltip',
    defaultMessage: 'Hide Sidebar',
    description:
      'Tooltip for the icon button that hides the sidebar in the ticket details header',
  },
  requestIdLabel: {
    id: 'TicketDetailsHeader.requestIdLabel',
    defaultMessage: 'Request ID: {requestId}',
    description: 'Label for the request ID in the ticket details header',
  },
});

type Props = {
  isLoading: boolean;
  ticketName: string;
  ticketId: string;
  sidebar: {
    isExpanded: boolean;
    onToggleSidebar: () => void;
  };
};

export const TicketDetailsHeader = ({
  isLoading,
  ticketName,
  ticketId,
  sidebar,
}: Props) => {
  const intl = useIntl();

  return (
    <Layout
      align="center"
      spacing={4}
      px={4}
      pb={4}
      borderBottom="border.divider"
    >
      <IconButton
        icon="bars"
        onClick={sidebar.onToggleSidebar}
        tooltip={
          sidebar.isExpanded
            ? intl.formatMessage(messages.hideSidebarTooltip)
            : intl.formatMessage(messages.showSidebarTooltip)
        }
      />
      <Layout direction="column" minW={0} spacing={1}>
        {isLoading ? (
          <LoadingShimmer
            width="300px"
            height={
              lineHeights[
                typography.title.lineHeight as keyof typeof lineHeights
              ]
            }
          />
        ) : (
          <TruncateText as="h1" variant="title" m={0}>
            {ticketName}
          </TruncateText>
        )}
        {isLoading ? (
          <LoadingShimmer
            height={
              lineHeights[
                typography.body.lineHeight as keyof typeof lineHeights
              ]
            }
          />
        ) : (
          <TruncateText
            as="p"
            variant="body"
            color="text.secondary"
            m={0}
            minW={0}
          >
            {intl.formatMessage(messages.requestIdLabel, {
              requestId: ticketId,
            })}
          </TruncateText>
        )}
      </Layout>
    </Layout>
  );
};
